import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Fetcher } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Link } from '@this/components/shared/ui/navigations/link';
import { Button } from '@this/shared/ui/inputs/button';
import {
  OrganizationBody,
  OrganizationTitle,
  OrganizationSubTitle,
  OrganizationNavBar,
  OrganizationTable,
  OrganizationTh,
  OrganizationButtonTh,
  OrganizationTd,
  OrganizationTdButton
} from '@this/components/organization/organization.style';
import { MasterListContainer } from '@this/components/organization/master_list_container';
import { DeleteMasterButton } from '@this/components/organization/delete-master-button';
import InvoiceCustomFormat from '@this/domain/invoice_custom_format/invoice_custom_format';
import type { InvoiceCustomFormatArgs } from '@this/domain/invoice_custom_format/invoice_custom_format';

interface Response {
  invoice_custom_formats: InvoiceCustomFormatArgs[];
}

const grayIfNeeded = (disabled: boolean) => ({
  background: disabled ? '#f2f2f3' : '#ffffff'
});

const InvoiceCustomFormats = observer(() => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [invoiceCustomFormats, setInvoiceCustomFormats] = useState<InvoiceCustomFormat[]>([]);

  const fetchInvoiceCustomFormats = async () => {
    setLoading(true);
    const response = await Fetcher.get<Response>('/organization/invoice_custom_formats.json');
    setInvoiceCustomFormats(response.invoice_custom_formats.map(raw => new InvoiceCustomFormat(raw)));
    setLoading(false);
  };

  useEffect(() => {
    fetchInvoiceCustomFormats();
  }, []);

  const handleRemoveFormat = async (format: InvoiceCustomFormat) => {
    await Fetcher.delete(`/organization/invoice_custom_formats/${format.id}.json`);
    fetchInvoiceCustomFormats();
  };

  const handleToggleEnabled = async (format: InvoiceCustomFormat) => {
    await Fetcher.put(`/organization/invoice_custom_formats/${format.id}.json`, { disabled: !format.disabled });
    fetchInvoiceCustomFormats();
  };

  return (
    <>
      <OrganizationNavBar>
        <Link href="/organization/invoices" style={{ display: 'flex', alignItems: 'center' }}>
          <ChevronLeft />
          請求書・明細に戻る
        </Link>
      </OrganizationNavBar>
      <OrganizationTitle>カスタムCSV</OrganizationTitle>
      <OrganizationSubTitle>新規でカスタムCSVを作成します。</OrganizationSubTitle>
      <OrganizationBody>
        <MasterListContainer
          createButton={
            <Button color="sub" href="/organization/invoice_custom_formats/new">
              <span style={{ marginRight: '5px' }}>カスタムCSVを追加</span>
              <AddCircleOutlineIcon />
            </Button>
          }
          renderContent={() => (
            <OrganizationTable>
              <thead>
                <tr>
                  <OrganizationTh>最終更新日時</OrganizationTh>
                  <OrganizationTh>ファイル名</OrganizationTh>
                  <OrganizationTh>状態</OrganizationTh>
                  <OrganizationButtonTh>有効/無効</OrganizationButtonTh>
                  <OrganizationButtonTh>編集</OrganizationButtonTh>
                  <OrganizationButtonTh>削除</OrganizationButtonTh>
                </tr>
              </thead>
              {loading ? (
                <tbody>
                  <tr>
                    <td colSpan={6}>
                      <Loading />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {invoiceCustomFormats.map(invoiceCustomFormat => (
                    <tr key={invoiceCustomFormat.id} style={grayIfNeeded(invoiceCustomFormat.disabled)}>
                      <OrganizationTd>
                        {invoiceCustomFormat.updatedAt?.format('YYYY-MM-DD HH:mm:ss')}
                      </OrganizationTd>
                      <OrganizationTd>{invoiceCustomFormat.name}</OrganizationTd>
                      <OrganizationTd>{invoiceCustomFormat.disabled ? '無効' : '有効'}</OrganizationTd>
                      <OrganizationTd>
                        <OrganizationTdButton onClick={() => handleToggleEnabled(invoiceCustomFormat)}>
                          {invoiceCustomFormat.disabled ? '有効' : '無効'}
                        </OrganizationTdButton>
                      </OrganizationTd>
                      <OrganizationTd>
                        <OrganizationTdButton
                          onClick={() =>
                            history.push(`/organization/invoice_custom_formats/${invoiceCustomFormat.id}/edit`)
                          }
                        >
                          編集
                        </OrganizationTdButton>
                      </OrganizationTd>
                      <OrganizationTd>
                        <DeleteMasterButton
                          targetLabel={invoiceCustomFormat.name}
                          onConfirm={() => handleRemoveFormat(invoiceCustomFormat)}
                        />
                      </OrganizationTd>
                    </tr>
                  ))}
                </tbody>
              )}
            </OrganizationTable>
          )}
        />
      </OrganizationBody>
    </>
  );
});

export default InvoiceCustomFormats;
